<template>
  <div class="account container">
    <loader v-show="isLoading"/>
    <div v-if="isLoaded" class="container">
      <div class="row">
        <before-form-header :title="$t('views.settings.myAccount')" description=""/>
        <div class="col-12">
          <b-tabs navWrapperClass="income-tabs" pills="" class="w-100">
            <b-tab v-for="i in steps" :key="'tab-' + i.id" :title="$t(i.title)" :active="i.id===step"
                   @click="setStep(i.id)"></b-tab>
          </b-tabs>
        </div>
        <div class="account-data col-12">
          <div class="row row-no-padding">
            <error class="col-12" :errors="errors"/>
            <b-alert class="col-12" variant="success" :show="!!successMessage" dismissible @dismissed="successMessage = ''">
              {{ successMessage }}
            </b-alert>
            <div class="main-info col-12" v-bind:class="step<7 ? ' col-md-8 col-lg-8 col-xl-8':''">
              <div class="info-content" v-show="(this.step===1)">
                <div class="info">
                  <div class="header">{{ $t('views.settings.accountId') }}</div>
                  <div class="detail">{{ investorDetails.investor_id }}</div>
                </div>
                <div v-if="investorDetails.investor_is_company !== 1">
                  <div class="info">
                    <div class="header">{{ $t('views.settings.firstName') }}</div>
                    <div class="detail">{{ investorDetails.investor_first_name }}</div>
                  </div>
                  <div class="info">
                    <div class="header">{{ $t('views.settings.lastName') }}</div>
                    <div class="detail">{{ investorDetails.investor_last_name }}</div>
                  </div>
                  <div class="info">
                    <div class="header">{{ $t('views.settings.city') }}</div>
                    <div class="detail">{{ investorDetails.investor_addr_city }}</div>
                  </div>
                  <div class="info">
                    <div class="header">{{ $t('views.settings.address') }}</div>
                    <div class="detail">{{ investorDetails.investor_addr_street }}</div>
                  </div>
                  <div class="info">
                    <div class="header">{{ $t('views.settings.postalCode') }}</div>
                    <div class="detail">{{ investorDetails.investor_addr_zip }}</div>
                  </div>
                  <div class="info">
                    <div class="header">{{ $t('views.settings.countryOfResidence') }}</div>
                    <div class="detail">{{ getCountryById(investorDetails.investor_addr_country) }}</div>
                  </div>
                </div>
                <div v-if="investorDetails.investor_is_company === 1">
                  <div class="info">
                    <div class="header">{{ $t('views.settings.companyName') }}</div>
                    <div class="detail">{{ investorDetails.investor_comp_name }}</div>
                  </div>
                  <div class="info">
                    <div class="header">{{ $t('views.settings.companyRegistrationNumber') }}</div>
                    <div class="detail">{{ investorDetails.investor_comp_reg_no }}</div>
                  </div>
                  <div class="info">
                    <div class="header">{{ $t('views.settings.companyRegisteredAddress') }}</div>
                    <div class="detail">{{
                        $t('views.settings.5344', {
                          '1': investorDetails.investor_comp_addr_street,
                          '2': investorDetails.investor_comp_addr_city,
                          '3': investorDetails.investor_comp_addr_zip,
                          '4': getCountryById(investorDetails.investor_comp_addr_country)
                        })
                      }}
                    </div>
                  </div>
                  <div class="info">
                    <div class="header">{{ $t('views.settings.representativeName') }}</div>
                    <div class="detail">{{ investorDetails.investor_first_name }} {{
                        investorDetails.investor_last_name
                      }}
                    </div>
                  </div>
                  <div class="info" v-if="this.investor_other_info">
                    <div class="header">{{ $t('views.settings.rightOfRepresentation') }}</div>
                    <div class="detail">{{ this.investor_other_info.right_of_representation }}</div>
                  </div>
                </div>
                <div class="info">
                  <div class="header">{{ $t('views.settings.phone') }}</div>
                  <div class="detail">{{ investorDetails.investor_phone }}</div>
                </div>
                <div class="info">
                  <div class="header">{{ $t('views.settings.email') }}</div>
                  <div class="detail">{{ investorDetails.investor_email }}</div>
                </div>
                <div v-if="!investorDetails.investor_is_company" class="info">
                  <div class="header">
                    {{ $t('createaccount.pin') }}
                    <help-tooltip :tooltipText="$t('createaccount.pinHelpText')"></help-tooltip>
                  </div>
                  <div class="detail pin">
                    <div v-if="!investorDetails.investor_pin">
                      <div v-show="isPinLoading">
                        <loader/>
                        <span class="m-2 pt-2 updating-label">updating...</span>
                      </div>
                      <b-input-group v-show="!isPinLoading" class="pin-form">
                        <b-form-input class="pin-input" v-model="investorDetails.pin" :maxlength="20"/>
                        <b-input-group-append>
                          <button class="btn-small pin-btn income-btn" @click="updatePin">Update</button>
                        </b-input-group-append>
                      </b-input-group>
                    </div>
                    <span v-if="investorDetails.investor_pin">
                          {{ investorDetails.investor_pin }}
                        </span>
                  </div>
                </div>
              </div>
              <div class="info-content" v-show="this.step===2">
                <div class="info">
                  <div class="header">{{ $t('views.settings.accessToken') }}</div>
                  <div class="detail">
                    <pre>{{ $auth.accessToken }}</pre>
                  </div>
                </div>
                <div class="info">
                  <div class="header">{{ $t('views.settings.authId') }}</div>
                  <div class="detail">
                    {{ investorDetails.investor_auth0_id }}
                  </div>
                </div>
                <div class="info">
                  <div class="header">{{ $t('views.settings.invitationCode') }}</div>
                  <div class="detail">
                    {{ investorDetails.invitation_code }}
                  </div>
                </div>
                <div class="info">
                  <div class="header">{{ $t('views.settings.identityStatus') }}</div>
                  <div class="detail">
                    {{ investorDetails.identity_status }}
                  </div>
                </div>
              </div>
              <div class="info-content" v-show="this.step===3">
                <div class="info">
                  <div class="header">{{ $t('views.settings.depositCurrency') }}</div>
                  <div class="detail">
                    {{
                      investorDetails.investor_deposit_currency ? investorDetails.investor_deposit_currency : getSettingsValue('deposit_currency')
                    }}
                  </div>
                </div>
                <div class="info">
                  <div class="header">{{ $t('views.settings.depositMethod') }}</div>
                  <div class="detail">
                    {{
                      investorDetails.investor_deposit_method ? investorDetails.investor_deposit_method : getSettingsValue('deposit_method')
                    }}
                  </div>
                </div>
              </div>
              <div class="info-content" v-show="this.step===4">
                <div class="account-settings-block">
                  <h5 class="account-settings-headline">{{ $t('views.settings.notifications') }}</h5>
                  <div class="note">{{ $t('views.settings.pleaseChooseHere') }}</div>
                  <loader v-show="notificationsUpdating"/>
                  <div class="notifications-list" v-if="!notificationsUpdating">
                    <ul>
                      <li>
                        <b-check v-model="subscriptionWeeklySummary"
                                 @change="updateSubscriptionInfo('WeeklySummary', $event)">
                          {{ $t('views.settings.weeklySummary') }}
                          <help-tooltip
                              :tooltipText="$t('views.settings.notificationsTooltips.weeklySummary')"></help-tooltip>
                        </b-check>
                      </li>
                      <li>
                        <b-check v-model="subscriptionMonthlySummary"
                                 @change="updateSubscriptionInfo('MonthlySummary', $event)">
                          {{ $t('views.settings.monthlySummary') }}
                          <help-tooltip
                              :tooltipText="$t('views.settings.notificationsTooltips.monthlySummary')"></help-tooltip>
                        </b-check>
                      </li>
                      <li>
                        <b-check v-model="subscriptionDeposit" @change="updateSubscriptionInfo('Deposit', $event)">
                          {{ $t('views.settings.deposit') }}
                          <help-tooltip
                              :tooltipText="$t('views.settings.notificationsTooltips.depositSummary')"></help-tooltip>
                        </b-check>
                      </li>
                      <li>
                        <b-check v-model="subscriptionWithdrawal"
                                 @change="updateSubscriptionInfo('Withdrawal', $event)">
                          {{ $t('views.settings.withdrawal') }}
                          <help-tooltip
                              :tooltipText="$t('views.settings.notificationsTooltips.withdrawalSummary')"></help-tooltip>
                        </b-check>
                      </li>
                      <li>
                        <b-check v-model="subscriptionBestResults"
                                 @change="updateSubscriptionInfo('BestResults', $event)">
                          {{ $t('views.settings.newsletters') }}
                          <help-tooltip
                              :tooltipText="$t('views.settings.notificationsTooltips.newslettersSummary')"></help-tooltip>
                        </b-check>
                      </li>
                    </ul>
                  </div>
                </div>
                <div class="account-settings-block account-settings-block--security">
                  <h5 class="account-settings-headline">{{ $t('views.settings.security') }}</h5>
                  <h6 class="account-settings-subheadline">{{ $t('views.settings.twoFactorAuth') }}</h6>
                  <loader v-show="twoFaLoading"/>

                  <div v-if="!twoFaLoading">
                    <b-alert dismissible :show="!!twoFaError" variant="danger" @dismissed="twoFaError = null">
                      {{ twoFaError }}
                    </b-alert>
                    <b-alert dismissible :show="!!twoFaSuccess" variant="success" @dismissed="twoFaSuccess = null">
                      {{ twoFaSuccess }}
                    </b-alert>

                    <div v-if="isEnrolled" class="twofa-status twofa-status--enabled">
                      <span class="twofa-badge twofa-badge--enabled">✓ {{ $t('views.settings.twoFaEnabled') }}</span>
                      <b-button
                        class="income-btn btn-sm twofa-btn twofa-btn--disable"
                        @click="investorDetails.investor_auth0_driver === 'auth0' ? showDisable2FaModal = true : disable2FA()"
                        :disabled="isRedirectLoading"
                      >
                        <b-spinner v-if="isRedirectLoading" small class="mr-1"></b-spinner>
                        {{ $t('views.settings.disable2Fa') }}
                      </b-button>
                    </div>

                    <div v-else class="twofa-status twofa-status--disabled">
                      <span class="twofa-badge twofa-badge--disabled">{{ $t('views.settings.twoFaDisabled') }}</span>

                      <div class="twofa-actions">
                        <b-button
                          class="income-btn btn-sm twofa-btn"
                          @click="investorDetails.investor_auth0_driver === 'auth0' ? showEnable2FaModal = true : enable2FA()"
                          :disabled="isRedirectLoading"
                        >
                          <b-spinner v-if="isRedirectLoading" small class="mr-1"></b-spinner>
                          {{ $t('views.settings.enable2Fa') }}
                        </b-button>
                        <b-button
                          v-if="twoFaEnrolmentStarted"
                          class="btn-sm twofa-btn twofa-btn--confirm ml-2"
                          @click="finalize2Fa"
                          :disabled="isFinalizeSubmitting"
                        >
                          <b-spinner v-if="isFinalizeSubmitting" small class="mr-1"></b-spinner>
                          {{ $t('views.settings.twoFaFinishedEnrolling') }}
                        </b-button>
                      </div>
                      <p v-if="twoFaEnrolmentStarted" class="twofa-hint">
                        {{ $t('views.settings.twoFaEnrolmentHint') }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="info-content" v-show="this.step===8">
                <div v-if="this.investorDetails.investor_is_file_upload_allowed">
                  <div class="ml-2 note">You can upload documents in this section. Please select the type of a document
                    you are uploading.
                  </div>
                  <loader v-show="notificationsUpdating"/>
                  <b-form-group v-for="(accountAttachment, key) in accountAttachments" :key="key">
                    <b-form-file
                        required
                        v-model="accountAttachment.file"
                        :placeholder="$t('companyprofile.chooseAFile1')"
                        :drop-placeholder="$t('companyprofile.dropFileHere1')"
                        class="form-field mt-2"
                    ></b-form-file>
                    <cool-select
                        required
                        v-model="accountAttachment.type"
                        :items="attachmentTypes"
                        item-value="value"
                        :onselect="checkUploadFileType(accountAttachment.type)"
                        item-text="label"
                        aria-required="true"
                        class="country-select country-select-extended form-field mt-2"
                        :placeholder="$t('companyprofile.attachmentType')"
                    >
                    </cool-select>

                  </b-form-group>
                  <div class="ml-2 note">{{ $t('companyprofile.attachmentDescription') }}</div>
                  <b-button class="income-btn refresh-btn btn-sm mr-2" @click="addFileToUpload()">
                    {{ $t('companyprofile.add') }}
                  </b-button>
                  <div class="ml-2 note">{{ $t('companyprofile.attachmentSendDescription') }}</div>
                  <b-button :disabled="isUploadWarning" class="income-btn refresh-btn btn-sm " @click="uploadFiles()">
                    {{ $t('companyprofile.send') }}
                  </b-button>
                  <b-alert dismissible :show="isUploadSuccessful" variant="success">
                    {{ $t('companyprofile.success') }}
                  </b-alert>
                  <b-alert dismissible :show="isUploadWarning" variant="warning">
                    {{ isUploadWarningMessage }}
                  </b-alert>
                  <div v-for="(files, key) in investorDetails.existingInvestorFileList" class="info"
                       :key="key + Math.random()">
                    <div class="header">{{ getAttachmentKey(key) }}</div>
                    <div class="detail">
                          <span v-for="file in files" :key="file + Math.random()">
                            <span class="mr-3">{{ file }}</span>
                          </span>
                    </div>
                  </div>

                </div>
                <div v-else>
                  <div class="info" v-if="this.investor_other_info.agreement_datetime">
                    <div>
                      {{ $t('companyprofile.uploadLocked')}}
                    </div>
                  </div>
                </div>
              </div>
              <div class="info-content" v-show="this.step===5">
                <div class="note">
                  <div class="info" v-if="this.investor_other_info.agreement_datetime">
                    <div>{{
                        $t('views.settings.userAgreementConcludedOn', {'1': this.investor_other_info.agreement_datetime})
                      }}
                    </div>
                  </div>
                </div>
                <div class="note">
                  <div class="info">
                    <div>{{ $t('views.settings.applicableVersionOf') }}
                      <router-link target="_blank" :to="'/' + $t('router.links.useragreement')">
                        {{ $t('views.settings.userAgreement') }}
                      </router-link>
                    </div>
                  </div>
                </div>
              </div>
              <div class="info-content" v-show="this.step===6">
                <div class="note">{{ $t('views.settings.yourInvestorAnnual') }}</div>
              </div>
              <div class="info-content" v-show="this.step===7">
                <loader v-show="isStatementLoading"/>
                <div v-if="!isStatementLoading">
                  <div class="filter-form">
                    <b-form inline>
                      <div class="info">
                        <div class="header">{{ $t('views.settings.investorName') }}</div>
                        <div class="detail">{{
                            investorDetails.investor_is_company ? investorDetails.investor_comp_name : investorDetails.investor_full_name
                          }}
                        </div>
                      </div>
                      <div v-if="investorDetails.investor_is_company" class="info">
                        <div class="header">{{ $t('views.settings.registrationNumber') }}</div>
                        <div class="detail">{{ investorDetails.investor_comp_reg_no }}</div>
                      </div>
                      <div v-if="investorDetails.investor_is_company" class="info">
                        <div class="header">{{ $t('createaccount.address') }}</div>
                        <div class="detail">{{ investorDetails.investor_comp_addr_street }}
                          {{ investorDetails.investor_comp_addr_zip }}
                          {{ investorDetails.investor_comp_addr_city }}
                          {{ getCountryById(investorDetails.investor_comp_addr_country) }}
                        </div>
                      </div>
                      <div v-else class="info">
                        <div class="header">{{ $t('createcompanyaccount.address') }}</div>
                        <div class="detail">{{ investorDetails.investor_addr_street }}
                          {{ investorDetails.investor_addr_zip }}
                          {{ investorDetails.investor_addr_city }}
                          {{ getCountryById(investorDetails.investor_addr_country) }}
                        </div>
                      </div>
                      <div class="info">
                        <div class="header">{{ $t('views.settings.currency') }}</div>
                        <div class="detail"> {{ currency }}</div>
                      </div>
                      <div class="w-30 filter-input">
                        <b-form-group
                            id="fieldset-horizontal"
                            content-cols-lg="7"
                            content-cols-sm
                            label-cols-lg="3"
                            label-cols-sm="4"
                            :label="$t('views.settings.bigFrom')"
                            label-for="startDate"
                        >
                          <date-pick v-model="startDate" :isDateDisabled="isFutureDate" format="DD/MM/YYYY"></date-pick>
                        </b-form-group>
                      </div>
                      <div class="w-30 filter-input">
                        <b-form-group
                            id="endDate"
                            content-cols-lg="7"
                            content-cols-sm
                            :label="$t('views.settings.to')"
                            label-cols-lg="3"
                            label-cols-sm="4"
                            label-for="endDate"
                        >
                          <date-pick v-model="endDate" :isDateDisabled="isFutureDate" format="DD/MM/YYYY"></date-pick>
                        </b-form-group>
                      </div>
                      <div>
                        <b-form-group
                            content-cols-lg="7"
                            content-cols-sm
                            label-cols-lg="3"
                            label-cols-sm="4"
                        >
                          <b-button class="income-btn refresh-btn btn-sm" @click="getStatement">
                            {{ $t('views.settings.refresh') }}
                          </b-button>
                        </b-form-group>
                      </div>
                      <div>
                        <b-form-group
                            content-cols-lg="7"
                            content-cols-sm
                            label-cols-lg="4"
                            label-cols-sm="4"
                        >
                          <b-button class="income-btn refresh-btn btn-sm"
                                    style="display: inline-block; white-space: nowrap; margin-left: 1rem;"
                                    @click="saveAs(1)">{{ $t('views.settings.account.saveAsPDF') }}
                          </b-button>
                        </b-form-group>
                      </div>
                      <div>
                        <b-form-group
                            content-cols-lg="7"
                            content-cols-sm
                            label-cols-lg="4"
                            label-cols-sm="4"
                        >
                          <b-button class="income-btn refresh-btn btn-sm"
                                    style="display: inline-block; white-space: nowrap; margin-left: 1.3rem;"
                                    @click="saveAs(3)">{{ $t('views.settings.account.saveTaxAsPDF') }}
                          </b-button>
                        </b-form-group>
                      </div>

                      <div class="w-30 filter-input">
                        <b-form-group
                            content-cols-lg="7"
                            content-cols-sm
                            label-cols-lg="4"
                            label-cols-sm="4"
                        >
                          <b-button class="income-btn refresh-btn btn-sm"
                                    style="display: inline-block; white-space: nowrap; margin-left: 2.3rem;"
                                    @click="saveAs(2)">{{ $t('views.settings.account.saveAsXls') }}
                          </b-button>
                        </b-form-group>
                      </div>
                    </b-form>
                  </div>
                  <div class="summary">
                    <b-table :fields="statementFields" :items="this.statement" responsive="sm" striped>
                      <template v-slot:head()="data">
                        <div>{{ $t(data.label) }}</div>
                      </template>
                      <template v-slot:cell(account_type_name)="{ value, item }">
                        <div class="">
                          <strong v-if="item.is_statement_field">{{
                              translateArray(item.account_type_name_i18n)
                            }}</strong>
                          <span v-else>{{ translateArray(item.account_type_name_i18n) }}</span>
                        </div>
                      </template>
                      <template v-slot:head(turnover2)="data">
                        <div class="text-right">{{ $t(data.label) }}</div>
                      </template>
                      <template v-slot:cell(turnover2)="{ value, item }">
                        <div v-if="item.is_statement_field" class="text-right"><strong>{{
                            value | moneyFormatter00
                          }}
                        </strong>
                        </div>
                        <div v-else class="text-right">{{
                            value | moneyFormatter00
                          }}
                        </div>
                      </template>
                      <template v-slot:cell(show_details)="row">
                        <div class="text-right">
                          <b-button v-show="!row.item.is_statement_field" class="" size="sm"
                                    variant="light"
                                    @click="row.toggleDetails">
                            {{ row.detailsShowing ? '&#x25B2;' : '&#x25BC;' }}
                          </b-button>
                          <b-form-checkbox v-model="row.detailsShowing" class="d-none" @change="row.toggleDetails">
                            {{ $t('views.settings.detailsViaCheck') }}
                          </b-form-checkbox>
                        </div>
                      </template>
                      <template v-slot:row-details="row">
                        <b-card>
                          <b-row class="mb-2">
                                <span v-if="row.item.turnover && !(row.item.turnover.length>0)">
                                 {{ $t('views.settings.noTurnoverIn') }}
                                </span>
                          </b-row>
                          <b-table v-if="row.item.turnover && (row.item.turnover.length>0)"
                                   :fields="statementDetailFields"
                                   :items="row.item.turnover">
                            <template v-slot:head()="data">
                              <div>{{ $t(data.label) }}</div>
                            </template>
                            <template v-slot:head(amount)="data">
                              <div class="text-right">{{ $t(data.label) }}</div>
                            </template>
                            <template v-slot:cell(amount)="{ value }">
                              <div class="text-right">{{ value | moneyFormatter00 }}</div>
                            </template>
                            <template v-slot:cell(date)="{ value }">
                              <div class="text-left">{{ getDateInFormat(value, 'DD/MM/YYYY') }}</div>
                            </template>
                            <template v-slot:cell()="data">
                              <div class="">{{ translateArray(data.item.Event_i18n) }}</div>
                            </template>
                            <template v-slot:cell(asset_id)="{ value }">
                              <router-link :to="{ path: '/loan/'+ value + '?step=3' }" target='_blank'>{{
                                  value
                                }}
                              </router-link>
                            </template>
                            <template v-slot:cell(lo_name)="{ value, item }">
                              <a :href="loanOriginatorPageUrl(value, item.loan_originator_page_url)" target="_blank">{{
                                  value
                                }}</a>
                            </template>
                          </b-table>
                          <b-button class="d-none" size="sm" @click="row.toggleDetails">
                            {{ $t('views.settings.hideDetails') }}
                          </b-button>
                        </b-card>
                      </template>
                    </b-table>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="this.step<7" class="col-12 support-section col-md-4 col-lg-4 col-xl-4">
              <div class="contacts">
                <div class="header">{{ $t('views.settings.support') }}</div>
                <div class="email"><a href="mailto:hello@getincome.com">{{ $t('views.settings.hellogetincomecom') }}</a>
                </div>
                <div class="phones">
                  <ul>
                    <li class="number">
                      <img :src="getFlag('EU')" class="country-flag" alt=""/>
                      <a href="callto:+441164649956">{{ $t('views.settings.5987') }}</a>
                    </li>
                    <!--                      <li class="number">-->
                    <!--                        <img :src="getFlag('DE')" class="country-flag" alt=""/>-->
                    <!--                        <a href="callto:+3726026813">{{ $t('views.settings.9864') }}</a>-->
                    <!--                      </li>-->
                    <li class="number">
                      <img :src="getFlag('EE')" class="country-flag" alt=""/>
                      <a href="callto:+3726600353">{{ $t('views.settings.9837') }}</a>
                    </li>
                    <!--                      <li class="number">-->
                    <!--                        <img :src="getFlag('ES')" class="country-flag" alt=""/>-->
                    <!--                        <a href="callto:+3726026807">{{ $t('views.settings.3480') }}</a>-->
                    <!--                      </li>-->
                    <!--                      <li class="number">-->
                    <!--                        <img :src="getFlag('IT')" class="country-flag" alt=""/>-->
                    <!--                        <a href="callto:+3726026854">{{ $t('views.settings.1342') }}</a>-->
                    <!--                      </li>-->
                    <!--                      <li class="number">-->
                    <!--                        <img :src="getFlag('US')" class="country-flag" alt=""/>-->
                    <!--                        <a href="callto:+12674332861">{{ $t('views.settings.2388') }}</a>-->
                    <!--                      </li>-->
                  </ul>
                  <div class="email"><a href="mailto:hello@getincome.com">{{
                      $t('views.settings.hellogetincomecom1')
                    }}</a></div>
                  <div class="work-info">
                    <div>{{ $t('views.settings.workdays_Am') }}</div>
                    <div>{{ $t('views.settings.weekendClosed') }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <b-modal
      v-model="showEnable2FaModal"
      :title="$t('views.settings.twoFaEnableTitle')"
      centered
      hide-footer
    >
      <div>
        <p>{{ $t('views.settings.twoFaEnablePasswordText') }}</p>
        <b-form-input
          type="password"
          v-model="twoFaPassword"
          :placeholder="$t('views.settings.twoFaEnterPassword')"
          class="mb-3"
        />
        <b-alert
          v-if="twoFaError"
          variant="danger"
          show
          dismissible
          @dismissed="twoFaError = null"
        >
          {{ twoFaError }}
        </b-alert>
        <div class="d-flex justify-content-end">
          <b-button
            variant="secondary"
            class="mr-2"
            @click="showEnable2FaModal = false"
          >
            {{ $t('views.settings.cancel') }}
          </b-button>
          <b-button
            class="income-btn"
            @click="confirmEnable2Fa"
            :disabled="isEnableSubmitting || !twoFaPassword"
          >
            <b-spinner v-if="isEnableSubmitting" small class="mr-1"></b-spinner>
            {{ $t('views.settings.confirm') }}
          </b-button>
        </div>
      </div>
    </b-modal>
    <b-modal
      v-model="showDisable2FaModal"
      :title="$t('views.settings.twoFaDisableTitle')"
      centered
      hide-footer
    >
      <div>
        <p>{{ $t('views.settings.twoFaDisablePasswordText') }}</p>
        <b-form-input
          type="password"
          v-model="twoFaPassword"
          :placeholder="$t('views.settings.twoFaEnterPassword')"
          class="mb-3"
        />
        <p>{{ $t('views.settings.twoFaOtpInstruction') }}</p>
        <b-form-input
          v-model="twoFaOtp"
          :placeholder="$t('views.settings.twoFaOtpPlaceholder')"
          class="mb-2"
        />
        <div class="text-muted text-center mb-2">OR</div>
        <b-form-input
          v-model="twoFaRecoveryCode"
          :placeholder="$t('views.settings.twoFaRecoveryPlaceholder')"
          class="mb-3"
        />
        <b-alert
          v-if="twoFaError"
          variant="danger"
          show
          dismissible
          @dismissed="twoFaError = null"
        >
          {{ twoFaError }}
        </b-alert>
        <div class="d-flex justify-content-end">
          <b-button
            variant="secondary"
            class="mr-2"
            @click="showDisable2FaModal = false"
          >
            {{ $t('views.settings.cancel') }}
          </b-button>
          <b-button
            class="income-btn"
            @click="confirmDisable2FA"
            :disabled="isOtpSubmitting || !twoFaPassword"
          >
            <b-spinner v-if="isOtpSubmitting" small class="mr-1"></b-spinner>
            {{ $t('views.settings.confirm') }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>
<script>
import BeforeFormHeader from '../components/BeforeFormHeader'
import Stepper from '../components/Stepper'
import Loader from '@/components/Loader'
import imageMixin from '@/mixins/image'
import flagMixin from '../mixins/flag'
import classificatorDataHelper from '@/helpers/classificatorDataHelper'
import investor from '@/api/investor'
import twoFactorAuthApi from '@/api/twoFactorAuth'
import Error from '@/components/Error'
import loadingMixin from '../mixins/loading'
import HelpTooltip from '@/components/HelpTooltip'
import DatePick from 'vue-date-pick'
import 'vue-date-pick/dist/vueDatePick.css'
import dateMixin from '../mixins/date'
import textMixin from '../mixins/text'
import axios from 'axios'
import {CoolSelect} from 'vue-cool-select'
import loanOriginatorPageUrl from '@/helpers/loanOriginatorLink'

export default {
  // eslint-disable-next-line vue/no-unused-components
  components: {CoolSelect, Error, Loader, Stepper, BeforeFormHeader, HelpTooltip, DatePick},
  mixins: [imageMixin, flagMixin, loadingMixin, dateMixin, textMixin],
  computed: {
    isLoaded: function ()
    {
      return this.loaded
    }
  },
  data()
  {
    return {
      settings: null,
      investor_other_info: null,
      investorDetails: this.$store.state.investors.investor,
      isLoading: true,
      step: 1,
      currency: '€(Euro)',
      steps: [
        {id: 1, title: 'views.settings.personalInformation', active: false, completed: false, image: 'personal-info.svg'},
        // {id: 2, title: 'Security', active: false, completed: false, image: 'security-info.svg'},
        // {id: 3, title: 'Currencies', active: false, completed: false, image: 'currency-info.svg'},
        {id: 4, title: 'views.settings.accountSettings', active: false, completed: false, image: 'notify-info.svg'},
        {id: 5, title: 'views.settings.agreements', active: false, completed: false, image: 'agreement-info.svg'},
        // {id: 6, title: 'Tax report', active: false, completed: false, image: 'tax-report-info.svg'}
        {id: 7, title: 'views.settings.accountStatement', active: false, completed: false, image: 'account-statement.svg'},
        {id: 8, title: 'views.settings.accountAttachments', active: false, completed: false, image: 'account-statement.svg'}
      ],
      subscriptionBestResults: false,
      subscriptionWeeklySummary: false,
      subscriptionMonthlySummary: false,
      subscriptionDeposit: false,
      subscriptionWithdrawal: false,
      notificationsUpdating: false,
      startDate: '',
      endDate: '',
      today: '',
      statement: [],
      openingBalance: 0,
      closingBalance: 0,
      accruedEarnings: 0,
      isStatementLoading: true,
      statementFields: [
        // {key: 'account_type', label: 'Account', class: 'table-header'},
        {key: 'account_type_name', label: 'views.settings.accountHeader', class: 'table-header'},
        // {key: 'opening_balance', label: 'views.settings.openingBalance', class: 'table-header'},
        {key: 'turnover2', label: 'views.settings.change', class: 'table-header'},
        // {key: 'closing_balance', label: 'views.settings.closingBalance', class: 'table-header'},
        {key: 'show_details', label: '', class: 'table-header'}
      ],
      statementDetailFields: [
        {key: 'Date', label: 'views.settings.date', class: 'table-header'},
        {key: 'Event', label: 'views.settings.events', class: 'table-header'},
        {key: 'asset_id', label: 'views.settings.assetId', class: 'table-header'},
        {key: 'lo_name', label: 'views.settings.loName', class: 'table-header'},
        {key: 'Amount', label: 'views.settings.amount', class: 'table-header'}
      ],
      errors: [],
      isPinLoading: false,
      accountAttachments: [
          {
        type: null,
        file: null
      }],
      attachmentTypes: [
        {
          label: this.$t('companyprofile.representativeAddress'),
          value: 'REPRESENTATIVE_ADDRESS'
        },
        {
          label: this.$t('companyprofile.UBOIdentificationDocument'),
          value: 'ID_DOC'
        },
        {
          label: this.$t('companyprofile.extractFromCompanyRegistry'),
          value: 'REGISTRY_EXTRACT'
        },
        {
          label: this.$t('companyprofile.articlesOfAssociation'),
          value: 'ASSOC_ARTICLE'
        },
        {
          label: this.$t('companyprofile.sourceOfFunds'),
          value: 'SOURCE_OF_FUNDS'
        }
      ],
      isUploadSuccessful: false,
      isUploadWarning: false,
      isUploadWarningMessage: '',
      isUploadWarningTypes: [],
      twoFaLoading: false,
      isOtpSubmitting: false,
      isEnableSubmitting: false,
      isFinalizeSubmitting: false,
      isRedirectLoading: false,
      isEnrolled: false,
      twoFaEnrolmentStarted: false,
      twoFaError: null,
      twoFaSuccess: null,
      showEnable2FaModal: false,
      showDisable2FaModal: false,
      twoFaPassword: '',
      twoFaOtp: '',
      twoFaRecoveryCode: '',
      successMessage: ''
    }
  },
  watch: {
    '$route.query.step': {
      handler: function (step)
      {
        if (step)
        {
          this.setStep(parseInt(step))
        }
      },
      deep: true,
      immediate: true
    },
    showEnable2FaModal(val) {
      if (!val) {
        this.twoFaPassword = ''
        this.twoFaError = null
      }
    },
    isLoaded: function ()
    {
      if (this.isLoaded)
      {
        this.investorDetails = this.$store.state.investors.investor
        if (this.investorDetails && this.investorDetails.settings)
        {
          this.settings = JSON.parse(this.investorDetails.settings)
        }
        if (this.investorDetails && this.investorDetails.investor_other_info)
        {
          this.investor_other_info = JSON.parse(this.investorDetails.investor_other_info)
          this.subscriptionBestResults = !!this.investor_other_info.subscription_best_results
          this.subscriptionWeeklySummary = !!this.investor_other_info.subscription_weekly_summary
          this.subscriptionMonthlySummary = !!this.investor_other_info.subscription_monthly_summary
          this.subscriptionDeposit = !!this.investor_other_info.subscription_deposit
          this.subscriptionWithdrawal = !!this.investor_other_info.subscription_withdrawal
        }
      }
      this.isLoading = !this.isLoaded
    },
    step: function (number)
    {
      this.applyStep(number)
      if (number === 4) {
        this.loadTwoFaStatus()
      }
    },
    '$store.state.investors.investor': function (investor)
    {
      if (investor)
      {
        if (investor && this.investorDetails && investor.investor_other_info)
        {
          this.investor_other_info = JSON.parse(this.investorDetails.investor_other_info)
          this.subscriptionBestResults = !!this.investor_other_info.subscription_best_results
          this.subscriptionWeeklySummary = !!this.investor_other_info.subscription_weekly_summary
          this.subscriptionMonthlySummary = !!this.investor_other_info.subscription_monthly_summary
          this.subscriptionDeposit = !!this.investor_other_info.subscription_deposit
          this.subscriptionWithdrawal = !!this.investor_other_info.subscription_withdrawal
        }
      }
    },
    subscriptionBestResults: function (checked)
    {
      this.$store.state.investors.investor.subscription_best_results = checked
    },
    subscriptionWeeklySummary: function (checked)
    {
      this.$store.state.investors.investor.subscription_weekly_summary = checked
    },
    subscriptionMonthlySummary: function (checked)
    {
      this.$store.state.investors.investor.subscription_monthly_summary = checked
    },
    subscriptionDeposit: function (checked)
    {
      this.$store.state.investors.investor.subscription_deposit = checked
    },
    subscriptionWithdrawal: function (checked)
    {
      this.$store.state.investors.investor.subscription_withdrawal = checked
    }
  },
  mounted()
  {
    this.isLoading = !this.isLoaded
    let defStep = 1
    if (this.$route && this.$route.query && this.$route.query.step)
    {
      defStep = parseInt(this.$route.query.step)
    }
    this.step = defStep
    this.applyStep(defStep)

    // Auto-finalize when the user returns to this tab after completing enrolment
    // on Auth0's hosted page. Without this, mfa_enabled stays false if the user
    // never clicks "I've finished enrolling".
    this._onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && this.twoFaEnrolmentStarted) {
        this.finalize2Fa()
      }
    }
    document.addEventListener('visibilitychange', this._onVisibilityChange)

    // Load 2FA status on mount
    if (this.step === 4) {
      this.loadTwoFaStatus()
    }

    // Handle redirect-back from Auth0 for social users.
    const params = this.$route.query
    if (params.mfa_enrolled === '1') {
      this.successMessage = this.$t('views.settings.twoFaEnabledSuccess')
      this.setStep(4)
      this.loadTwoFaStatus()
      this.cleanMfaQuery()
    } else if (params.mfa_disabled === '1') {
      this.successMessage = this.$t('views.settings.twoFaDisabledSuccess')
      this.setStep(4)
      this.loadTwoFaStatus()
      this.cleanMfaQuery()
    } else if (params.mfa_error === '1') {
      this.errors = [this.$t('views.settings.twoFaGenericError')]
      this.setStep(4)
      this.cleanMfaQuery()
    }

    const d = new Date()
    this.startDate = this.getDateInFormat(new Date(d.getFullYear(), d.getMonth(), 1), 'DD/MM/YYYY')
    this.today = this.endDate = this.getDateInFormat(new Date(d.getFullYear(), d.getMonth(), d.getDate()), 'DD/MM/YYYY')
    this.getStatement()
    investor.getInvestor().then(response =>
    {
      this.investorDetails = {...this.$store.state.investors.investor, ...response}
      this.$store.dispatch('investors/updateInvestor', this.investorDetails)
    })
  },
  beforeDestroy()
  {
    if (this._onVisibilityChange) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange)
    }
  },
  filters: {
    moneyFormatter00: function (value)
    {
      if (value === '' || value === null || typeof value === 'undefined')
      {
        return value
      }
      const parsed = parseFloat(value)
      if (isNaN(parsed))
      {
        console.error('failed to convert input to number:', value)
        return 'NaN'
      }
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2
      })
      return formatter.format(parsed)
    }
  },
  methods: {
    loanOriginatorPageUrl,
    setStep(number)
    {
      this.step = number
    },
    applyStep(number)
    {
      try
      {
        // Set completed previous step
        this.steps.filter(s => s.id < number).forEach(function (step)
        {
          step.active = false
          // step.completed = true
        })
        // Set not active/completed next elements
        this.steps.filter(s => s.id > number).forEach(function (step)
        {
          step.active = false
          step.completed = false
        })
        // Set active current selected step
        this.steps.filter(s => s.id === number).shift().active = true
      }
      catch (e)
      {
        console.log('Something went wrong with changing steps', e)
      }
    },
    getSettingsValue(fieldName)
    {
      if (this.settings && this.settings[fieldName])
      {
        return this.settings[fieldName]
      }
    },
    getCountryById(countryId)
    {
      return classificatorDataHelper.getNameById('COUNTRY', countryId)
    },
    saveAs(type) {
      let message = ''
      if (type === 1)
      {
        message = this.$t('views.settings.account.saveAsPDFMessage')
      }
      if (type === 2)
      {
        message = this.$t('views.settings.account.saveAsXLSMessage')
      }

      this.$bvToast.toast(message, {
        title: '',
        toaster: 'b-toaster-top-center',
        variant: 'info',
        autoHideDelay: 5000
      })

      investor.getAccountStatement(
        this.startDate ? this.getDateInFormat(this.startDate, 'YYYY-MM-DD', 'DD/MM/YYYY') : 0,
        this.endDate ? this.getDateInFormat(this.endDate, 'YYYY-MM-DD', 'DD/MM/YYYY') : 0,
        type
      ).then(response =>
        {
          if (type === 1) {
            const linkSource = `data:application/pdf;base64,${response}`
            const downloadLink = document.createElement('a')
            const fileName = 'turnover_' + this.startDate + '_' + this.endDate + '.pdf'
            downloadLink.href = linkSource
            downloadLink.download = fileName
            downloadLink.click()
          }

          if (type === 3) {
            const linkSource = `data:application/pdf;base64,${response}`
            const downloadLink = document.createElement('a')
            const fileName = 'tax_' + this.startDate + '_' + this.endDate + '.pdf'
            downloadLink.href = linkSource
            downloadLink.download = fileName
            downloadLink.click()
          }

          if (type === 2) {
            const linkSource = `data:application/xls;base64,${response}`
            const downloadLink = document.createElement('a')
            const fileName = 'turnover_' + this.startDate + '_' + this.endDate + '.xlsx'
            downloadLink.href = linkSource
            downloadLink.download = fileName
            downloadLink.click()
          }
        })
    },
    getStatement()
    {
      this.isStatementLoading = true
      this.statement = []
      investor.getAccountStatement(
          this.startDate ? this.getDateInFormat(this.startDate, 'YYYY-MM-DD', 'DD/MM/YYYY') : 0,
          this.endDate ? this.getDateInFormat(this.endDate, 'YYYY-MM-DD', 'DD/MM/YYYY') : 0
      ).then(response =>
      {
        this.statement.push({
          account_type_name: `Opening balance as at ${this.startDate}`,
          account_type_name_i18n: ['views.settings.openingBalanceAsAt', this.startDate],
          turnover2: response.opening_balance,
          is_statement_field: true
        })
        this.statement = this.statement.concat(response.statement)
        this.statement.push({
          account_type_name: `Closing balance as at ${this.endDate}`,
          account_type_name_i18n: ['views.settings.closingBalanceAsAt', this.endDate],
          turnover2: response.closing_balance,
          is_statement_field: true
        })
        this.statement.push({
          account_type_name: `Accrued interest as at ${this.endDate}`,
          account_type_name_i18n: ['views.settings.accruedInterest', this.endDate],
          turnover2: response.accrued_earnings,
          is_statement_field: true
        })

        this.statement.push({
          account_type_name: `Invested balance as at ${this.endDate}`,
          account_type_name_i18n: ['views.settings.investedBalance', this.endDate],
          turnover2: response.invested_balance,
          is_statement_field: true
        })
        this.openingBalance = response.opening_balance
        this.closingBalance = response.closing_balance
        this.accruedEarnings = response.accrued_earnings
        this.isStatementLoading = false
      }).catch(error =>
      {
        // console.error(error)
        if (error.response && error.response.data)
        {
          if (error.response.status && error.response.status === 401)
          {
            // hard redirect if got 401 error
            window.location.href = '/'
          } else
          {
            this.errors.push(error.response.data)
          }
        }
        this.isStatementLoading = false
      })
    },
    updateSubscriptionInfo(type, value)
    {
      this.notificationsUpdating = true
      // Additional update for test-server
      switch (type)
      {
        case 'WeeklySummary':
          this.subscriptionWeeklySummary = value
          break
        case 'MonthlySummary':
          this.subscriptionMonthlySummary = value
          break
        case 'Deposit':
          this.subscriptionDeposit = value
          break
        case 'Withdrawal':
          this.subscriptionWithdrawal = value
          break
        case 'BestResults':
          this.subscriptionBestResults = value
          break
      }
      const data = {
        subscription_best_results: this.subscriptionBestResults,
        subscription_weekly_summary: this.subscriptionWeeklySummary,
        subscription_monthly_summary: this.subscriptionMonthlySummary,
        subscription_deposit: this.subscriptionDeposit,
        subscription_withdrawal: this.subscriptionWithdrawal
      }
      investor.update(data)
          .then(() =>
          {
            this.notificationsUpdating = false
            this.investor_other_info.subscription_best_results = this.subscriptionBestResults
            this.investor_other_info.subscription_weekly_summary = this.subscriptionWeeklySummary
            this.investor_other_info.subscription_monthly_summary = this.subscriptionMonthlySummary
            this.investor_other_info.subscription_deposit = this.subscriptionDeposit
            this.investor_other_info.subscription_withdrawal = this.subscriptionWithdrawal
            this.$store.state.investors.investor.investor_other_info = JSON.stringify(this.investor_other_info)
          })
          .catch((error) =>
          {
            console.error(error)
            this.notificationsUpdating = false
          })
    },
    updatePin()
    {
      const data = {
        pin: this.investorDetails.pin
      }
      this.isPinLoading = true
      investor.update(data)
          .then(() =>
          {
            this.investorDetails.investor_pin = this.investorDetails.pin
            // console.log('Updated. Set:', this.investorDetails.investor_pin)
            this.isPinLoading = false
          })
          .catch((error) =>
          {
            this.isPinLoading = false
            console.error(error)
          })
    },
    addFileToUpload() {
      this.accountAttachments.push({
        file: null,
        type: null
      })
    },
    checkUploadFileType(item) {
      console.log(item)
      if (this.accountAttachments.filter(e => e.type === item).length > 4) {
        this.isUploadWarning = true
        this.isUploadWarningMessage = this.$t('companyprofile.uploadLimit')
      } else {
        this.isUploadWarning = false
      }
    },
    uploadFiles() {
      const formData = new FormData()

      for (const accountAttachment of this.accountAttachments) {
        formData.append(accountAttachment.type + '[]', accountAttachment.file)
      }

      axios.post('/upload-profile-files',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
      ).then((mainResponse) =>
      {
        console.log(mainResponse.data.message)
        if (mainResponse.data.message) {
          this.isUploadWarning = true
          this.isUploadWarningMessage = mainResponse.data.message
        }
        else
        {
          this.isUploadSuccessful = true
        }

        investor.getInvestor().then(response =>
        {
          this.investorDetails = {...this.$store.state.investors.investor, ...response}
          this.$store.dispatch('investors/updateInvestor', this.investorDetails)
        })

        this.accountAttachments = [{
          file: null,
          type: null
        }]
      })
    },
    translateArray(translateObject)
    {
      if (Array.isArray(translateObject))
      {
        if (translateObject.length === 1) {
          return this.$t(translateObject[0])
        } else if (translateObject.length === 2 && translateObject[0] && translateObject[1])
        {
          return this.$t(translateObject[0]) + ' ' + translateObject[1]
        } else
        {
          // console.log('Skipped translation for object: ', translateObject)
          let result = ''
          for (let i = 0; i < translateObject.length; ++i) {
            if (i > 0) {
              result += ' '
            }
            if (this.$t(translateObject[i]) !== translateObject[i])
            {
              result += this.$t(translateObject[i])
            } else {
              result += translateObject[i]
            }
          }
          return result
        }
      } else {
        return this.$t(translateObject)
      }
    },
    makeToast(text, variant = null)
    {
      this.$bvToast.toast(text, {
        title: text,
        variant: variant,
        solid: true,
        toaster: 'b-toaster-top-center'
      })
    },
    isFutureDate(date)
    {
      const currentDate = new Date()
      return date > currentDate
    },
    getAttachmentKey(key) {
      const result = this.attachmentTypes.find(f => f.value === key)
      if (result) {
        return result.label
      }
    },
    async loadTwoFaStatus() {
      this.twoFaLoading = true
      this.twoFaError = null
      try {
        const result = await twoFactorAuthApi.finalize()
        this.isEnrolled = !!result.is_enrolled
      } catch (e) {
        this.twoFaError = this.$t('views.settings.twoFaLoadError')
      } finally {
        this.twoFaLoading = false
      }
    },
    async confirmEnable2Fa() {
      if (this.isEnableSubmitting) return
      this.isEnableSubmitting = true
      this.twoFaError = null

      const enrollmentWindow = window.open('', '_blank')

      try {
        const result = await twoFactorAuthApi.enable(this.twoFaPassword)
        const enrollmentUrl = result.enrollment_url

        this.showEnable2FaModal = false
        this.twoFaPassword = ''

        if (enrollmentWindow) {
          enrollmentWindow.opener = null
          enrollmentWindow.location.href = enrollmentUrl
          this.twoFaEnrolmentStarted = true
        } else {
          this.twoFaError = this.$t('views.settings.twoFaEnableError')
        }

      } catch (e) {
        if (enrollmentWindow) enrollmentWindow.close()
        if (e.response?.data?.error) {
          this.twoFaError = e.response.data.error
        } else if (e.response?.data?.errors?.password) {
          this.twoFaError = e.response.data.errors.password[0]
        } else {
          this.twoFaError = this.$t('views.settings.twoFaEnableError')
        }
      } finally {
        this.isEnableSubmitting = false
      }
    },
    async finalize2Fa() {
      if (this.isFinalizeSubmitting) return
      this.isFinalizeSubmitting = true
      this.twoFaError = null
      try {
        const result = await twoFactorAuthApi.finalize()
        this.isEnrolled = !!result.is_enrolled
        if (this.isEnrolled) {
          this.twoFaEnrolmentStarted = false
          this.twoFaSuccess = this.$t('views.settings.twoFaEnabledSuccess')
        } else {
          this.twoFaError = this.$t('views.settings.twoFaEnrolmentIncomplete')
        }
      } catch (e) {
        this.twoFaError = this.$t('views.settings.twoFaLoadError')
      } finally {
        this.isFinalizeSubmitting = false
      }
    },
    async enable2FA() {
      const isDbUser = this.investorDetails.investor_auth0_driver === 'auth0'

      if (isDbUser) {
        this.showEnable2FaModal = true
        return
      }

      if (this.isRedirectLoading) return
      this.isRedirectLoading = true
      try {
        const result = await twoFactorAuthApi.enableViaRedirect('/settings')
        window.location.assign(result.authorize_url)
      } catch (err) {
        if (err.response && err.response.status === 409) {
          this.errors = [this.$t('views.settings.twoFaAlreadyEnabled')]
        } else {
          this.errors = [this.$t('views.settings.twoFaGenericError')]
        }
        this.isRedirectLoading = false
      }
    },
    async disable2FA() {
      const isDbUser = this.investorDetails.investor_auth0_driver === 'auth0'

      if (isDbUser) {
        this.showDisable2FaModal = true
        return
      }

      // Social user — redirect flow.
      if (this.isRedirectLoading) return
      this.isRedirectLoading = true
      try {
        const result = await twoFactorAuthApi.disableViaRedirect('/settings')
        if (result.redirect_required) {
          window.location.assign(result.authorize_url)
        } else {
          await this.loadTwoFaStatus()
          this.successMessage = this.$t('views.settings.twoFaDisabledSuccess')
          this.isRedirectLoading = false
        }
      } catch (err) {
        this.errors = [this.$t('views.settings.twoFaGenericError')]
        this.isRedirectLoading = false
      }
    },
    cleanMfaQuery() {
      const next = { ...this.$route.query, step: '4' }
      delete next.mfa_enrolled
      delete next.mfa_disabled
      delete next.mfa_error
      this.$router.replace({ query: next })
    },
    async confirmDisable2FA() {
      if (this.isOtpSubmitting) return
      this.isOtpSubmitting = true
      this.twoFaError = null

      try {
        const result = await twoFactorAuthApi.disable(
          this.twoFaPassword || undefined,
          this.twoFaOtp || undefined,
          this.twoFaRecoveryCode || undefined
        )

        this.isEnrolled = result.is_enrolled
        this.twoFaSuccess = this.$t('views.settings.twoFaDisabledSuccess')

        this.showDisable2FaModal = false
        this.twoFaOtp = ''
        this.twoFaRecoveryCode = ''
        this.twoFaPassword = ''

      } catch (e) {
        this.twoFaError =
          e.response?.data?.error ||
          e.response?.data?.message ||
          this.$t('views.settings.twoFaDisableError')
      } finally {
        this.isOtpSubmitting = false
      }
    }
  }
}
</script>

<style lang="scss">
.vertical-steps {
  max-width: 250px;

  .stepper {
    ul {
      display: grid;
    }
  }
}
.pin {
  .full-height-section {
    height: auto !important;
    float: left;
  }

  .loading-spinner {
    zoom: 0.2 !important;
    max-width: 240px;
  }

  .pin-form {
    .form-control, .custom-file-label, .custom-select {
      min-height: 32px;
    }
  }
}
</style>
<style scoped lang="scss">
.label:first-letter {
  text-transform: capitalize;
}
.pin {
  .pin-form {
    width: 185px;

    .pin-input {
      padding-left: 10px;
      max-width: 155px;
    }

    .pin-btn {
      border-radius: 0 12px 12px 0;
    }
  }
  .updating-label{
    height: 32px;
    display: block;
  }
}
.vertical-steps {
  float: left;
  margin: 10px;
}

.account-data {
  min-height: 400px;
  margin-top: 10px;
  font-size: 13px;
}

.info {
  border-bottom: 1px solid #EDEFF2;
  width: 100%;
  display: inline-block;
  padding: 5px;
  margin-bottom: 6px;
  margin-top: 5px;

  .header {
    width: 35%;
    float: left;
    font-style: normal;
    font-weight: normal;
    font-size: 13px;
    line-height: 20px;
    color: #000000;
    @media (max-width: 798px) {
      width: 40%;
    }
  }

  .detail {
    width: 100%;
    font-weight: bold;
    font-size: 13px;
    line-height: 20px;
    color: #000000;
  }
}

.contacts {
  background: #FFFFFF;
  /* Shadow */
  box-shadow: 0 4px 16px 1px rgba(48, 167, 254, 0.1);
  border-radius: 20px;
  border: 1px solid #EDEFF2;
  padding: 15px;

  .header {
    font-weight: bold;
    margin: 5px 5px 10px;
  }

  .phones {
    padding-left: 5px;
    font-size: 12px;

    ul {
      list-style: none;
      padding-left: 0px;
    }
  }

  .country-flag {
    margin-right: 10px;
    border: 1px solid #eaecf0;
    background-size: cover;
    background-position: top center;
    border-radius: 50%;
    width: 20px;
    height: 20px;
  }
}

.work-info {
  margin-top: 8px;
  margin-bottom: 8px;
}

.note {
  font-size: 13px;
  margin-top: 15px;
  margin-bottom: 15px;
  text-align: justify;
}

.notifications-list {
  ul {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      margin-top: 8px;
      margin-bottom: 8px;
    }
  }
}

.filter-form {
  margin-bottom: 15px;

  .refresh-btn {
    padding:0.15rem 0.5rem !important;
    margin-left: 10px;
    /*margin-top: 15px;*/
  }
  .filter-input {
    margin-right: 21px;
  }
}

.main-info{
  background: #FFFFFF;
  box-shadow: 0px 4px 16px 1px rgba(48, 167, 254, 0.1);
  border-radius: 20px;
  padding-right: -10px;
  margin-bottom: 20px;

  .info-content{
    padding-left: 10px;
    padding-right: 10px;
    margin-right: 5px;
  }
}

.support-section{
  @media (min-width:1200px) {
    margin-left: 15px;
    max-width: 330px;
  }
  @media (min-width:798px) and (max-width:1200px) {
    margin-left: 15px;
    max-width: 205px;
  }
}
 .country-select-extended {
   height: calc(2.2em + .75rem)!important;
   padding: .175rem .7rem!important;
 }

.account-settings-block {
  padding: 16px 10px 12px;
  border-bottom: 1px solid #EDEFF2;
  margin-bottom: 4px;

  &:last-child {
    border-bottom: none;
  }

  &--security {
    padding-top: 20px;
  }
}

.account-settings-headline {
  font-size: 15px;
  font-weight: 600;
  color: #000;
  margin-bottom: 12px;
}

.account-settings-subheadline {
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.twofa-status {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.twofa-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;

  &--enabled {
    background: #e6f7ee;
    color: #1a7a45;
    border: 1px solid #b3e6cc;
  }

  &--disabled {
    background: #f5f5f5;
    color: #888;
    border: 1px solid #ddd;
  }
}

.twofa-description {
  font-size: 13px;
  color: #555;
  margin: 4px 0 8px;
}

.twofa-hint {
  font-size: 12px;
  color: #888;
  margin-top: 6px;
}

.twofa-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.twofa-btn {
  font-size: 13px;

  &--disable {
    background: transparent !important;
    color: #dc3545 !important;
    border: 1px solid #dc3545 !important;
    &:hover {
      background: #dc3545 !important;
      color: #fff !important;
    }
  }

  &--confirm {
    background: #e8f5e9 !important;
    color: #2e7d32 !important;
    border: 1px solid #a5d6a7 !important;
    &:hover {
      background: #2e7d32 !important;
      color: #fff !important;
    }
  }
}
</style>
